import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiClock, FiRefreshCw, FiMessageSquare,
  FiThumbsUp, FiThumbsDown, FiX,
} from 'react-icons/fi';
import '../../styles/QuoteProposal.css';
import '../commissionscan/CommissionScan.css';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import { useToast } from '../../context/ToastContext';
const PAGE_SIZE = 10;
const getToken  = () => localStorage.getItem('toolboxToken') || '';

const formatDate = (d: string): string => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

interface AppetiteHistoryRow {
  transactionId: string;
  policyType: string;
  state: string;
  naicsCode: string;
  created: string;
  userName: string;
  appetiteResponse: string;
  isLiked: boolean | null;
  totalCount: number;
}

interface CarrierItem {
  carrierName: string;
  carrierLogoURL: string;
}

const RateRiskAppetiteHistory: React.FC = () => {
  const [rows, setRows]             = useState<AppetiteHistoryRow[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview */
  const [previewCarriers, setPreviewCarriers] = useState<CarrierItem[] | null>(null);
  const [previewPolicyType, setPreviewPolicyType] = useState('');

  /* Feedback */
  const [feedbackRowId,      setFeedbackRowId]      = useState<string | null>(null);
  const [feedbackText,       setFeedbackText]        = useState('');
  const [feedbackRating,     setFeedbackRating]      = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]      = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting]  = useState(false);
  const [feedBackError,      setFeedBackError]       = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  /* Toast */
  const { showSuccess, showError } = useToast();

  /* ── Fetch ── */
  const fetchData = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/RateAppetite/appetiteTransactions?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const data: AppetiteHistoryRow[] = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      setTotalCount(data.length > 0 ? (data[0].totalCount ?? data.length) : 0);
    } catch (e: any) {
      setError(e.message || 'Unknown error while getting appetite history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(page); }, [fetchData, page]);
  useEffect(() => { setPage(1); }, [search]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.policyType?.toLowerCase().includes(q) ||
      r.state?.toLowerCase().includes(q) ||
      r.naicsCode?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  /* ── Sorting ── */
  type SortKey = 'policyType' | 'state' | 'naicsCode' | 'created' | 'userName';
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const onSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (sortKey === 'created') {
        const av = a.created ? new Date(a.created).getTime() : 0;
        const bv = b.created ? new Date(b.created).getTime() : 0;
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const av = (a[sortKey] || '').toString().toLowerCase();
      const bv = (b[sortKey] || '').toString().toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const renderSortableTh = (label: string, key: SortKey) => (
    <th
      className="qp-table__th"
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(key)}
      title={`Sort by ${label}`}
    >
      {label}
      <span style={{ marginLeft: 5, fontSize: 9, opacity: sortKey === key ? 1 : 0.35 }}>
        {sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
      </span>
    </th>
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  /* ── Preview ── */
  const onPreviewClick = useCallback((row: AppetiteHistoryRow) => {
    if (!row.appetiteResponse) return;
    try {
      const parsed = JSON.parse(row.appetiteResponse);
      const list: CarrierItem[] = parsed?.providerInfo?.primaryPreferrefProvider || [];
      setPreviewCarriers(list);
      setPreviewPolicyType(row.policyType);
    } catch {
      setPreviewCarriers([]);
      setPreviewPolicyType(row.policyType);
    }
  }, []);

  /* ── Feedback ── */
  const openFeedback = useCallback((row: AppetiteHistoryRow, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackRowId(row.transactionId);
    setFeedbackText('');
    setFeedbackRating(row.isLiked ?? null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackRowId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
  }, []);

  const saveRating = useCallback((id: string, isLiked: boolean | null) => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/RateAppetite/appetiteratings`,
      { TransactionId: id, IsLiked: isLiked },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => showSuccess('Feedback submitted successfully!')).catch(() => showError('Unknown error while submitting ratings'));
  }, [showSuccess, showError]);

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.transactionId === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.transactionId === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onSubmitFeedback = useCallback(() => {
    if (!feedbackRowId) return;
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please provide your feedback within 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/RateAppetite/appetitefeedback`,
      { TransactionId: feedbackRowId, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => { setFeedbackSubmitting(false); showError('Unknown error while submitting feedback'); });
  }, [feedbackRowId, feedbackText, closeFeedback, showSuccess, showError]);

  useEffect(() => {
    if (!feedbackRowId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackRowId, closeFeedback]);

  /* ── Render ── */
  return (
    <>
      <motion.div
        className="qp-history"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div className="qp-history__header">
          <h3 className="qp-history__title" style={{ margin: 0 }}>Rate Risk Appetite History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="cs-client-search__input-wrap" style={{ minWidth: 220 }}>
              <FiSearch size={13} className="cs-client-search__icon" />
              <input
                className="cs-client-search__input"
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <motion.button
              className="csh-refresh-btn"
              onClick={() => fetchData(page)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        {/* Body */}
        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state" style={{ color: 'var(--accent-red)' }}>
              <FiClock size={28} /><p>{error}</p>
            </div>
          ) : loading ? (
            <div className="qp-history-loader" style={{ minHeight: 280 }}>
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiClock size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching rate risk appetite records
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiClock size={28} />
              <p>{search ? 'No records match your search.' : 'No appetite queries yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    {renderSortableTh('Policy Type', 'policyType')}
                    {renderSortableTh('State', 'state')}
                    {renderSortableTh('NAICS Code', 'naicsCode')}
                    {renderSortableTh('Submitted Date', 'created')}
                    {renderSortableTh('User', 'userName')}
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Action</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row, i) => (
                    <tr key={row.transactionId ?? i} className="qp-table__row">
                      <td className="qp-table__td">{row.policyType || '—'}</td>
                      <td className="qp-table__td">{row.state || '—'}</td>
                      <td className="qp-table__td">{row.naicsCode || '—'}</td>
                      <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>{formatDate(row.created)}</td>
                      <td className="qp-table__td">{row.userName || '—'}</td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <div className="qp-table__actions">
                          <button
                            className="qp-table__action-btn"
                            title="Preview"
                            onClick={() => onPreviewClick(row)}
                          >
                            <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                          </button>
                        </div>
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <button
                          className="qp-table__action-btn"
                          title="Feedback"
                          onClick={e => openFeedback(row, e)}
                          style={{ margin: '0 auto' }}
                        >
                          <FiMessageSquare size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button
                        key={p}
                        className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                        onClick={() => setPage(p as number)}
                      >{p}</button>
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Feedback popup — portalled to body */}
      {feedbackRowId && feedbackAnchor && ReactDOM.createPortal(
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          {feedBackError && (
            <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 4, display: 'block' }}>
              {feedBackError}
            </span>
          )}
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              className="cs-browse-btn cs-browse-btn--sm"
              onClick={onSubmitFeedback}
              disabled={feedbackSubmitting}
              style={{ flex: 1, justifyContent: 'center' }}
            >
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
            <button
              onClick={closeFeedback}
              style={{
                flex: 1, padding: '7px 16px', fontSize: 12, fontWeight: 600,
                background: 'var(--surface-glass)', color: 'var(--text-secondary)',
                border: '1px solid var(--border-glass)', borderRadius: 8, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body,
      )}

      {/* Preview modal — portalled to body */}
      {previewCarriers !== null && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setPreviewCarriers(null)}
        >
          <div
            style={{
              background: 'var(--bg-secondary, #fff)', borderRadius: 16,
              border: '1px solid var(--border-glass, #e5e7eb)',
              padding: 24, width: '80vw', maxWidth: 700, maxHeight: '80vh',
              overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 16, paddingBottom: 14,
              borderBottom: '1px solid var(--border-glass, #e5e7eb)',
            }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                Available Carriers — {previewPolicyType}
              </h3>
              <button
                onClick={() => setPreviewCarriers(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', display: 'flex' }}
              >
                <FiX size={18} />
              </button>
            </div>

            {previewCarriers.length === 0 ? (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 13, textAlign: 'center', padding: '24px 0', margin: 0 }}>
                No carrier information available.
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {previewCarriers.map((c, i) => (
                  <div
                    key={i}
                    style={{
                      background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                      borderRadius: 10, padding: '10px 16px',
                      display: 'flex', alignItems: 'center', gap: 10, minWidth: 160,
                    }}
                  >
                    {c.carrierLogoURL ? (
                      <img
                        src={c.carrierLogoURL}
                        alt={c.carrierName}
                        style={{
                          height: 32, maxWidth: 80, objectFit: 'contain',
                          background: '#fff', borderRadius: 6, padding: 2,
                        }}
                        onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : null}
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                      {c.carrierName}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default RateRiskAppetiteHistory;

